import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import Loader from '../../components/Common/Loader';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

interface FundamentalSummary {
    symbole: string;
    nom_societe: string | null;
    exercice: number;
    period_type: string;
    closing_date: string | null;
    is_complete: boolean;
    updated_at: string | null;
}

interface IFundamental {
    societe_symbole: string;
    exercice: number;
    period_type: string;
    closing_date: string | null;
    revenue: number | null;
    ebitda: number | null;
    ebit: number | null;
    net_income: number | null;
    net_income_reported: number | null;
    equity: number | null;
    financial_debt: number | null;
    cash: number | null;
    net_debt: number | null;
    commentaires: string | null;
}

interface Stock {
    symbole: string;
    nom_societe: string;
}

const PERIOD_TYPES = [
    { value: 'ANNUAL', label: 'Annuel (12 mois)' },
    { value: 'Q1', label: 'Trimestre 1 (Q1 - 3 mois)' },
    { value: 'S1', label: 'Semestre 1 (S1 - 6 mois)' },
    { value: 'Q3', label: 'Trimestre 3 (Q3 - 9 mois)' }
];

// Generate fiscal year options (current year back to 2015)
const currentYear = new Date().getFullYear();
const FISCAL_YEARS = Array.from({ length: currentYear - 2015 + 1 }, (_, i) => currentYear - i);

// Multipliers for unit conversion
const UNIT_MULTIPLIERS = [
    { value: 1, label: 'Unités (FCFA)', shortLabel: '1' },
    { value: 1000, label: 'Milliers (K FCFA)', shortLabel: 'K' },
    { value: 1000000, label: 'Millions (M FCFA)', shortLabel: 'M' },
    { value: 1000000000, label: 'Milliards (Mds FCFA)', shortLabel: 'Mds' }
];

const AdminFundamentalsScreen: React.FC = () => {
    const navigate = useNavigate();
    const { token } = useAuth();
    const [fundamentals, setFundamentals] = useState<FundamentalSummary[]>([]);
    const [stocks, setStocks] = useState<Stock[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Filters
    const [filterExercice, setFilterExercice] = useState<number | null>(null);
    const [filterPeriodType, setFilterPeriodType] = useState<string | null>(null);

    // Sorting
    const [sortColumn, setSortColumn] = useState<'symbole' | 'exercice' | 'updated_at'>('symbole');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [isEditing, setIsEditing] = useState(false);
    // Composite key for editing (symbole, exercice, period_type)
    const [editingKey, setEditingKey] = useState<{ symbole: string; exercice: number; period_type: string } | null>(null);

    // Unit multiplier for data entry
    const [unitMultiplier, setUnitMultiplier] = useState(1000000); // Default: Millions

    // Form state
    const [formData, setFormData] = useState({
        societe_symbole: '',
        exercice: currentYear,
        period_type: 'ANNUAL',
        revenue: '',
        ebitda: '',
        ebit: '',
        net_income: '',
        net_income_reported: '',
        equity: '',
        financial_debt: '',
        cash: '',
        commentaires: ''
    });

    const fetchFundamentals = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/fundamentals`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setFundamentals(data);
            }
        } catch (error) {
            console.error('Error fetching fundamentals:', error);
        }
        setLoading(false);
    }, [token]);

    const fetchStocks = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/v1/stocks/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setStocks(data);
            }
        } catch (error) {
            console.error('Error fetching stocks:', error);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchFundamentals();
            fetchStocks();
        }
    }, [token, fetchFundamentals, fetchStocks]);

    const filteredData = useMemo(() => {
        let data = fundamentals;

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            data = data.filter(f =>
                f.symbole.toLowerCase().includes(term) ||
                (f.nom_societe || '').toLowerCase().includes(term)
            );
        }

        // Apply exercice filter
        if (filterExercice !== null) {
            data = data.filter(f => f.exercice === filterExercice);
        }

        // Apply period type filter
        if (filterPeriodType !== null) {
            data = data.filter(f => f.period_type === filterPeriodType);
        }

        // Apply sorting
        data = [...data].sort((a, b) => {
            let comparison = 0;
            switch (sortColumn) {
                case 'symbole':
                    comparison = a.symbole.localeCompare(b.symbole);
                    break;
                case 'exercice':
                    comparison = (a.exercice || 0) - (b.exercice || 0);
                    break;
                case 'updated_at':
                    comparison = (a.updated_at ? new Date(a.updated_at).getTime() : 0) - (b.updated_at ? new Date(b.updated_at).getTime() : 0);
                    break;
            }
            return sortDirection === 'asc' ? comparison : -comparison;
        });

        return data;
    }, [fundamentals, searchTerm, filterExercice, filterPeriodType, sortColumn, sortDirection]);

    // Get unique exercices from data for filter dropdown
    const availableExercices = useMemo(() => {
        const exercices = [...new Set(fundamentals.map(f => f.exercice).filter(e => e))];
        return exercices.sort((a, b) => b - a); // Descending order
    }, [fundamentals]);

    const openCreateModal = (symbole?: string) => {
        setIsEditing(false);
        setEditingKey(null);
        setStep(1);
        setUnitMultiplier(1000000); // Default to Millions
        setFormData({
            societe_symbole: symbole || '',
            exercice: currentYear,
            period_type: 'ANNUAL',
            revenue: '',
            ebitda: '',
            ebit: '',
            net_income: '',
            net_income_reported: '',
            equity: '',
            financial_debt: '',
            cash: '',
            commentaires: ''
        });
        setIsModalOpen(true);
    };

    const handleOpenEditModal = async (f: FundamentalSummary) => {
        // If no data exists yet (is_complete=false with no real updated_at), open create modal
        if (!f.is_complete && !f.exercice) {
            openCreateModal(f.symbole);
            return;
        }

        setIsEditing(true);
        setEditingKey({ symbole: f.symbole, exercice: f.exercice, period_type: f.period_type });
        setStep(1);
        setUnitMultiplier(1000000); // Affichage en Millions lors de l'édition

        // Fetch full data
        try {
            const response = await fetch(`${API_URL}/v1/admin/fundamentals/${f.symbole}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data: IFundamental[] = await response.json();
                // Find by composite key
                const fund = data.find(d => d.societe_symbole === f.symbole && d.exercice === f.exercice && d.period_type === f.period_type);
                if (fund) {
                    // Diviser par 1000000 pour afficher en Millions (lisible)
                    const toM = (v: number | null | undefined): string =>
                        v !== null && v !== undefined ? (v / 1000000).toString() : '';
                    setFormData({
                        societe_symbole: fund.societe_symbole,
                        exercice: fund.exercice,
                        period_type: fund.period_type,
                        revenue: toM(fund.revenue),
                        ebitda: toM(fund.ebitda),
                        ebit: toM(fund.ebit),
                        net_income: toM(fund.net_income),
                        net_income_reported: toM(fund.net_income_reported),
                        equity: toM(fund.equity),
                        financial_debt: toM(fund.financial_debt),
                        cash: toM(fund.cash),
                        commentaires: fund.commentaires || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching fundamental details:', error);
        }
        setIsModalOpen(true);
    };

    // Convert form value to real value using multiplier
    const toRealValue = (value: string): number | null => {
        if (!value || value.trim() === '') return null;
        const numValue = parseFloat(value.replace(',', '.'));
        if (isNaN(numValue)) return null;
        return Math.round(numValue * unitMultiplier);
    };

    // Rescale all numeric form fields when unit changes (prevents corruption)
    const handleUnitChange = (newMultiplier: number) => {
        if (newMultiplier === unitMultiplier) return;
        const rescale = (val: string): string => {
            if (!val || val.trim() === '') return val;
            const num = parseFloat(val.replace(',', '.'));
            if (isNaN(num)) return val;
            return (num * unitMultiplier / newMultiplier).toString();
        };
        setFormData(prev => ({
            ...prev,
            revenue: rescale(prev.revenue),
            ebitda: rescale(prev.ebitda),
            ebit: rescale(prev.ebit),
            net_income: rescale(prev.net_income),
            net_income_reported: rescale(prev.net_income_reported),
            equity: rescale(prev.equity),
            financial_debt: rescale(prev.financial_debt),
            cash: rescale(prev.cash),
        }));
        setUnitMultiplier(newMultiplier);
    };

    const handleSave = async () => {
        // Convert all values using the selected multiplier
        const payload = {
            societe_symbole: formData.societe_symbole,
            exercice: formData.exercice,
            period_type: formData.period_type,
            revenue: toRealValue(formData.revenue),
            ebitda: toRealValue(formData.ebitda),
            ebit: toRealValue(formData.ebit),
            net_income: toRealValue(formData.net_income),
            net_income_reported: toRealValue(formData.net_income_reported),
            equity: toRealValue(formData.equity),
            financial_debt: toRealValue(formData.financial_debt),
            cash: toRealValue(formData.cash),
            commentaires: formData.commentaires
        };

        try {
            // URL uses composite key for editing
            const url = isEditing && editingKey
                ? `${API_URL}/v1/admin/fundamentals/${editingKey.symbole}/${editingKey.exercice}/${editingKey.period_type}`
                : `${API_URL}/v1/admin/fundamentals`;

            const response = await fetch(url, {
                method: isEditing ? 'PUT' : 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setIsModalOpen(false);
                fetchFundamentals();
            } else {
                const error = await response.json();
                const msg = typeof error.detail === 'string'
                    ? error.detail
                    : Array.isArray(error.detail)
                        ? error.detail.map((e: any) => `${e.loc?.join('.')} — ${e.msg}`).join('\n')
                        : JSON.stringify(error);
                alert(`Erreur lors de la sauvegarde :\n${msg}`);
            }
        } catch (error) {
            console.error('Error saving fundamental:', error);
            alert('Erreur réseau ou réponse invalide. Vérifiez la console.');
        }
    };

    const handleRemoveEntry = async (id: number) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée ?')) return;

        try {
            const response = await fetch(`${API_URL}/v1/admin/fundamentals/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                fetchFundamentals();
            }
        } catch (error) {
            console.error('Error deleting fundamental:', error);
        }
    };

    // Calculate Net Debt in real-time (with multiplier)
    const calculatedNetDebt = useMemo(() => {
        const debt = toRealValue(formData.financial_debt);
        const cash = toRealValue(formData.cash);
        if (debt !== null && cash !== null) {
            return debt - cash;
        }
        return null;
    }, [formData.financial_debt, formData.cash, unitMultiplier]);

    const formatNumber = (n: number | null) => {
        if (n === null) return '—';
        return n.toLocaleString('fr-FR');
    };

    const formatCompactNumber = (n: number | null) => {
        if (n === null) return '—';
        const abs = Math.abs(n);
        if (abs >= 1000000000) {
            return (n / 1000000000).toFixed(1) + ' Mds';
        } else if (abs >= 1000000) {
            return (n / 1000000).toFixed(1) + ' M';
        } else if (abs >= 1000) {
            return (n / 1000).toFixed(1) + ' K';
        }
        return n.toLocaleString('fr-FR');
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('fr-FR');
    };

    // Get current unit label
    const currentUnitLabel = UNIT_MULTIPLIERS.find(u => u.value === unitMultiplier)?.shortLabel || '';

    return (
        <SiteLayout>
            <Header icon="analytics" title="Données Fondamentales" />

            <div style={{ padding: '20px' }}>
                <Box>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={{ margin: 0, fontSize: '18px' }}>📊 Gestion des Données Fondamentales</h2>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => openCreateModal()}
                                style={{
                                    background: '#2563eb',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <span>+ Nouveau</span>
                            </button>
                            <button
                                onClick={() => fetchFundamentals()}
                                style={{
                                    background: '#f3f4f6',
                                    color: '#374151',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                🔄 Rafraîchir
                            </button>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="Rechercher par symbole ou nom..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                maxWidth: '300px',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db'
                            }}
                        />

                        {/* Exercice Filter */}
                        <select
                            value={filterExercice ?? ''}
                            onChange={(e) => setFilterExercice(e.target.value ? parseInt(e.target.value) : null)}
                            style={{
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                background: '#fff',
                                minWidth: '120px'
                            }}
                        >
                            <option value="">Tous les exercices</option>
                            {availableExercices.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>

                        {/* Period Type Filter */}
                        <select
                            value={filterPeriodType ?? ''}
                            onChange={(e) => setFilterPeriodType(e.target.value || null)}
                            style={{
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                background: '#fff',
                                minWidth: '140px'
                            }}
                        >
                            <option value="">Toutes les périodes</option>
                            {PERIOD_TYPES.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>

                        {/* Active filters count */}
                        {(filterExercice !== null || filterPeriodType !== null) && (
                            <button
                                onClick={() => {
                                    setFilterExercice(null);
                                    setFilterPeriodType(null);
                                }}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: '#fef2f2',
                                    color: '#dc2626',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                }}
                            >
                                ✕ Réinitialiser filtres
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <Loader message="Chargement des données..." />
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                        <th
                                            style={{ padding: '12px', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => {
                                                if (sortColumn === 'symbole') {
                                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                                } else {
                                                    setSortColumn('symbole');
                                                    setSortDirection('asc');
                                                }
                                            }}
                                        >
                                            Symbole {sortColumn === 'symbole' && (sortDirection === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>Dernière période</th>
                                        <th
                                            style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => {
                                                if (sortColumn === 'exercice') {
                                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                                } else {
                                                    setSortColumn('exercice');
                                                    setSortDirection('desc');
                                                }
                                            }}
                                        >
                                            Année {sortColumn === 'exercice' && (sortDirection === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th style={{ padding: '12px', textAlign: 'center' }}>Complétude</th>
                                        <th
                                            style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => {
                                                if (sortColumn === 'updated_at') {
                                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                                } else {
                                                    setSortColumn('updated_at');
                                                    setSortDirection('desc');
                                                }
                                            }}
                                        >
                                            Dernière MAJ {sortColumn === 'updated_at' && (sortDirection === 'asc' ? '▲' : '▼')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.map(f => (
                                        <tr
                                            key={f.symbole}
                                            onClick={() => navigate(`/admin/fundamentals/${f.symbole}`)}
                                            style={{
                                                borderBottom: '1px solid #e5e7eb',
                                                cursor: 'pointer',
                                                transition: 'background 0.15s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = ''}
                                        >
                                            <td style={{ padding: '12px', fontWeight: 'bold' }}>
                                                {f.symbole}
                                                <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 'normal' }}>
                                                    {f.nom_societe}
                                                </div>
                                            </td>
                                            <td style={{ padding: '12px' }}>{f.period_type}</td>
                                            <td style={{ padding: '12px', textAlign: 'center' }}>
                                                {f.exercice}
                                            </td>
                                            <td style={{ padding: '12px', textAlign: 'center' }}>
                                                {!f.exercice ? (
                                                    <span style={{ color: '#9ca3af' }}>—</span>
                                                ) : f.is_complete ? (
                                                    <span style={{
                                                        background: '#dcfce7',
                                                        color: '#166534',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px'
                                                    }}>
                                                        ✅ Complète
                                                    </span>
                                                ) : (
                                                    <span style={{
                                                        background: '#fef3c7',
                                                        color: '#92400e',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px'
                                                    }}>
                                                        ⚠️ Incomplète
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: '#6b7280' }}>
                                                {f.exercice && f.updated_at ? formatDate(f.updated_at) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Box>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '12px',
                        width: '100%',
                        maxWidth: '700px',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        padding: '24px'
                    }}>
                        <h2 style={{ margin: '0 0 20px 0' }}>
                            {isEditing ? '✏️ Modifier' : '➕ Nouvelle entrée'} - Étape {step}/2
                        </h2>

                        {/* Step Indicator */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                            <div style={{
                                flex: 1,
                                height: '4px',
                                background: step >= 1 ? '#2563eb' : '#e5e7eb',
                                borderRadius: '2px'
                            }} />
                            <div style={{
                                flex: 1,
                                height: '4px',
                                background: step >= 2 ? '#2563eb' : '#e5e7eb',
                                borderRadius: '2px'
                            }} />
                        </div>

                        {step === 1 && (
                            <div>
                                <h3 style={{ marginBottom: '16px', fontSize: '15px', color: '#374151' }}>
                                    🔹 Identification de la période
                                </h3>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
                                        Société *
                                    </label>
                                    <select
                                        value={formData.societe_symbole}
                                        onChange={(e) => setFormData({ ...formData, societe_symbole: e.target.value })}
                                        disabled={isEditing}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '6px',
                                            border: '1px solid #d1d5db',
                                            background: isEditing ? '#f3f4f6' : '#fff'
                                        }}
                                    >
                                        <option value="">-- Sélectionner une société --</option>
                                        {stocks.map(s => (
                                            <option key={s.symbole} value={s.symbole}>
                                                {s.symbole} - {s.nom_societe}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
                                        Type de période
                                    </label>
                                    <select
                                        value={formData.period_type}
                                        onChange={(e) => setFormData({ ...formData, period_type: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '6px',
                                            border: '1px solid #d1d5db'
                                        }}
                                    >
                                        {PERIOD_TYPES.map(p => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
                                        Exercice Fiscal *
                                    </label>
                                    <select
                                        value={formData.exercice}
                                        onChange={(e) => setFormData({ ...formData, exercice: parseInt(e.target.value) })}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '6px',
                                            border: '1px solid #d1d5db'
                                        }}
                                    >
                                        {FISCAL_YEARS.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
                                    <button
                                        onClick={() => setIsModalOpen(false)}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '6px',
                                            border: '1px solid #d1d5db',
                                            background: '#fff',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        onClick={() => setStep(2)}
                                        disabled={!formData.societe_symbole || !formData.exercice}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            background: '#2563eb',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            opacity: (!formData.societe_symbole || !formData.exercice) ? 0.5 : 1
                                        }}
                                    >
                                        Suivant →
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div>
                                <h3 style={{ marginBottom: '16px', fontSize: '15px', color: '#374151' }}>
                                    🔹 Données Financières
                                </h3>

                                {/* Unit Multiplier Selector */}
                                <div style={{
                                    background: '#eff6ff',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    marginBottom: '20px',
                                    border: '1px solid #bfdbfe'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '13px', color: '#1e40af', fontWeight: 500 }}>
                                            💡 Les montants sont exprimés en :
                                        </span>
                                        <select
                                            value={unitMultiplier}
                                            onChange={(e) => handleUnitChange(Number(e.target.value))}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #93c5fd',
                                                background: '#fff',
                                                fontSize: '13px',
                                                fontWeight: 500
                                            }}
                                        >
                                            {UNIT_MULTIPLIERS.map(u => (
                                                <option key={u.value} value={u.value}>{u.label}</option>
                                            ))}
                                        </select>
                                        <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                            (Sélectionnez l'unité du rapport que vous consultez)
                                        </span>
                                    </div>
                                </div>

                                {/* Compte de résultat */}
                                <div style={{ marginBottom: '24px' }}>
                                    <h4 style={{
                                        fontSize: '14px',
                                        color: '#374151',
                                        marginBottom: '16px',
                                        borderBottom: '2px solid #2563eb',
                                        paddingBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        📈 Compte de Résultat
                                    </h4>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#6b7280' }}>
                                                <strong>Chiffre d'Affaires</strong>
                                                <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>(Revenue / CA)</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={formData.revenue}
                                                    onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                                                    placeholder="Ex: 206,2"
                                                    style={{ width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                                />
                                                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af' }}>
                                                    {currentUnitLabel}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#6b7280' }}>
                                                <strong>EBITDA</strong>
                                                <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>(Excédent Brut d'Exploitation)</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={formData.ebitda}
                                                    onChange={(e) => setFormData({ ...formData, ebitda: e.target.value })}
                                                    placeholder="Ex: 42,6"
                                                    style={{ width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                                />
                                                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af' }}>
                                                    {currentUnitLabel}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#6b7280' }}>
                                                <strong>EBIT</strong>
                                                <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>(Résultat d'Exploitation)</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={formData.ebit}
                                                    onChange={(e) => setFormData({ ...formData, ebit: e.target.value })}
                                                    placeholder="Ex: 35,2"
                                                    style={{ width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                                />
                                                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af' }}>
                                                    {currentUnitLabel}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#6b7280' }}>
                                                <strong>Résultat Net</strong>
                                                <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>(Net Income)</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={formData.net_income}
                                                    onChange={(e) => setFormData({ ...formData, net_income: e.target.value })}
                                                    placeholder="Ex: 24,1"
                                                    style={{ width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                                />
                                                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af' }}>
                                                    {currentUnitLabel}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#6b7280' }}>
                                                <strong>RN Reporté</strong>
                                                <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>(Part Grp / HAO...)</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={formData.net_income_reported}
                                                    onChange={(e) => setFormData({ ...formData, net_income_reported: e.target.value })}
                                                    placeholder="Ex: 23,8"
                                                    style={{ width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f9fafb' }}
                                                />
                                                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af' }}>
                                                    {currentUnitLabel}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bilan */}
                                <div style={{ marginBottom: '24px' }}>
                                    <h4 style={{
                                        fontSize: '14px',
                                        color: '#374151',
                                        marginBottom: '16px',
                                        borderBottom: '2px solid #16a34a',
                                        paddingBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        📊 Bilan
                                    </h4>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#6b7280' }}>
                                                <strong>Capitaux Propres</strong>
                                                <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>(Equity)</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={formData.equity}
                                                    onChange={(e) => setFormData({ ...formData, equity: e.target.value })}
                                                    placeholder="Ex: 128,9"
                                                    style={{ width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                                />
                                                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af' }}>
                                                    {currentUnitLabel}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#6b7280' }}>
                                                <strong>Dettes Financières</strong>
                                                <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>(Financial Debt)</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={formData.financial_debt}
                                                    onChange={(e) => setFormData({ ...formData, financial_debt: e.target.value })}
                                                    placeholder="Ex: 7,3"
                                                    style={{ width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                                />
                                                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af' }}>
                                                    {currentUnitLabel}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#6b7280' }}>
                                                <strong>Trésorerie Actif</strong>
                                                <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>(Cash)</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={formData.cash}
                                                    onChange={(e) => setFormData({ ...formData, cash: e.target.value })}
                                                    placeholder="Ex: 19,0"
                                                    style={{ width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                                />
                                                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af' }}>
                                                    {currentUnitLabel}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Commentaires */}
                                    <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#6b7280' }}>
                                            <strong>Commentaires / Notes d'extraction</strong>
                                        </label>
                                        <textarea
                                            value={formData.commentaires || ''}
                                            onChange={(e) => setFormData({ ...formData, commentaires: e.target.value })}
                                            placeholder="Ex: Chiffres consolidés. Le résultat net inclut les éléments exceptionnels."
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: '1px solid #d1d5db',
                                                height: '60px',
                                                fontSize: '13px',
                                                resize: 'vertical'
                                            }}
                                        />
                                    </div>

                                    {/* Net Debt (calculated) */}
                                    <div style={{
                                        background: calculatedNetDebt !== null && calculatedNetDebt < 0 ? '#dcfce7' : '#fef3c7',
                                        padding: '14px 16px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        border: calculatedNetDebt !== null && calculatedNetDebt < 0 ? '1px solid #86efac' : '1px solid #fcd34d'
                                    }}>
                                        <div>
                                            <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                                                👉 Dette Nette (Net Debt)
                                            </span>
                                            <span style={{ display: 'block', fontSize: '11px', color: '#6b7280' }}>
                                                = Dettes Financières - Trésorerie
                                            </span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{ fontWeight: 'bold', fontSize: '16px', color: calculatedNetDebt !== null && calculatedNetDebt < 0 ? '#166534' : '#92400e' }}>
                                                {calculatedNetDebt !== null ? formatCompactNumber(calculatedNetDebt) + ' FCFA' : '—'}
                                            </span>
                                            {calculatedNetDebt !== null && calculatedNetDebt < 0 && (
                                                <span style={{ display: 'block', fontSize: '11px', color: '#166534' }}>
                                                    ✅ Cash net (trésorerie positive)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '24px' }}>
                                    <button
                                        onClick={() => setStep(1)}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '6px',
                                            border: '1px solid #d1d5db',
                                            background: '#fff',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        ← Retour
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            background: '#16a34a',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontWeight: 500
                                        }}
                                    >
                                        💾 Enregistrer
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </SiteLayout>
    );
};

export default AdminFundamentalsScreen;
